import React, { useEffect, createContext, useState, useContext } from "react";
import PropTypes from "prop-types";
import axios from "../http_utils/axiosConfig";
import { useToast } from "src/context/ToastContext";
import { INACTIVE_USER_ERROR_CODE } from "src/http_utils/errorCodes";

export const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const { addToast } = useToast();
  const [user, setUser] = useState(null);
  const [userGroup, setUserGroup] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const clearAll = () => {
    setUser(null);
    setIsAuthenticated(false);
    setUserGroup(null);
  };

  useEffect(() => {
    const validateUser = async () => {
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");
      if (!accessToken && !refreshToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/users/validate-user`, {});
        if (response.data.success) {
          setUser(response.data.user);
          setIsAuthenticated(true);
          setUserGroup(response.data.user.group);
        }
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.error_code == INACTIVE_USER_ERROR_CODE
        ) {
          addToast(`${error.response.data.error}`, {
            title: "Error",
            type: "error",
          });
        }
        clearAll();
      } finally {
        setLoading(false);
      }
    };

    validateUser();
  }, []);

  const login = async (requestBody) => {
    const response = await axios.post(`/users/login`, requestBody);
    if (response.status === 200) {
      const { access_token, refresh_token, user } = response.data;
      localStorage.setItem("accessToken", access_token);
      localStorage.setItem("refreshToken", refresh_token);
      setUser(user);
      setIsAuthenticated(true);
      setUserGroup(user.group);
    } else {
      throw new Error(response.data.error || "Unknown error occurred");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        login,
        loading,
        userGroup,
        clearAll,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
